import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { Messages } from "../../../../../../configuration/event-messages.constants";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { PortCallFormModel, PortCallStatusEnum, PortCallStatusHistoryItemModel, VesselPortCallRiskScoreModel, VesselPreArrivalDetailsModel, VesselPreArrivalSteps } from "../../models/ports-and-terminals.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../../../../utilities/reflection";
import { VettingType } from "../../../../../../models/vetting/vetting.models";
import { Router } from "aurelia-router";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { VesselDetailModel } from "../../../../../../models/vessels/vessel.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { AutoScreeningService } from "../../../../../../services/vetting/auto-screening.service";
import { useView } from "aurelia-framework";

@autoinject()
@transient()
@useView('views/pages/modules/ports-and-terminals/vessel-pre-arrival/steps/vessel-pre-arrival-summary.html')
export class VesselPreArrivalSummary {

    distributionColElements: Element[] = [];
    vesselPreArrivalDetails: VesselPreArrivalDetailsModel;
    vetRiskScores: VesselPortCallRiskScoreModel[];
    highestRiskLevel: string;
    maxCategoryCount: number;
    horizontalBarTicks: number;
    reassignment: number = PortCallStatusEnum.Reassignment;
    isShowMoreSectionStatus: boolean;
    defaultStatusesCountToShow: number = 3;
    isReadMoreSectionStatus: { [key: string]: boolean } = {};
    refreshVesselPreArrival: (params: { vesselIds: number[], vesselPortCallId: number }) => void;

    public get distributionColElementBarWidth(): number {
        return this.distributionColElements[0] ? this.distributionColElements[0].clientWidth / 5 : 0;
    }

    public vetRiskScoresLegends: { name: string, class: string }[] = [
        { name: "Low Risk", class: "low-risk" },
        { name: "Medium Risk", class: "medium-risk" },
        { name: "High Risk", class: "high-risk" }
    ];

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalSummary>(x => x.vesselPreArrivalDetails))
    public get canRequestVet(): boolean {
        return this.userClaimsService.hasPermission(Claims.CanLinkAutoScreeningWithFullVet) &&
            new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) > new Date() &&
            this.vesselPreArrivalDetails.vetId != null;
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalSummary>(x => x.vesselPreArrivalDetails))
    public get validForms(): PortCallFormModel[] {
        return this.vesselPreArrivalDetails ? this.vesselPreArrivalDetails.forms.filter(x => x.isValid) : [];
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrivalSummary>(x => x.vesselPreArrivalDetails))
    public get lastFiveForms(): PortCallFormModel[] {
        return this.vesselPreArrivalDetails
            ? Enumerable.from(this.vesselPreArrivalDetails.forms)
                .orderByDescending(x => new Date(x.submittedOn))
                .take(5)
                .toArray()
            : [];
    }

    constructor(private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator,
        private manageVesselService: ManageVesselService,
        private autoScreeningService: AutoScreeningService,
        private userClaimsService: UserClaimsService,
        private router: Router) {
    }

    activate(model: { vesselPreArrivalDetails: VesselPreArrivalDetailsModel, vetRiskScores: VesselPortCallRiskScoreModel[], refreshVesselPreArrival: (params: { vesselIds: number[], vesselPortCallId: number }) => void }) {
        this.vesselPreArrivalDetails = model.vesselPreArrivalDetails;
        this.vetRiskScores = Enumerable.from(model.vetRiskScores)
            .where(x => x.name != 'All')
            .where(x => x.name != 'Critical')
            .where(x => x.name != 'Soft')
            .orderByDescending(x => x.countHigh).thenByDescending(x => x.countMedium).thenByDescending(x => x.countLow).toArray();
        this.refreshVesselPreArrival = model.refreshVesselPreArrival;
        this.highestRiskLevel = Enumerable.from(model.vetRiskScores).orderByDescending(x => x.riskLevelId).select(x => x.riskLevel).firstOrDefault();
        this.maxCategoryCount = Enumerable.from(model.vetRiskScores).select(x => x.countHigh + x.countLow + x.countMedium).orderByDescending(x => x).firstOrDefault();
        this.horizontalBarTicks = Math.ceil(this.maxCategoryCount / 5.0);
    }

    openFormsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortCallFormsDialog,
            {
                vesselSKey: this.vesselPreArrivalDetails.vesselSKey,
                vesselName: this.vesselPreArrivalDetails.vesselName,
                vesselImoNumber: this.vesselPreArrivalDetails.imo
            }, true, false, false, "right");
    }

    openRiskAnalysisRulesDialog(vetRiskScore: VesselPortCallRiskScoreModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RiskAnalysisRulesDialog,
            {
                riskCategoryId: vetRiskScore.riskCategoryId,
                categoryName: vetRiskScore.name,
                vetId: this.vesselPreArrivalDetails.vetId,
                vesselName: this.vesselPreArrivalDetails.vesselName,
                vesselIdentifier: this.vesselPreArrivalDetails.identifier
            }, true, false, false, "right");
    }

    showMoreSectionStatus() {
        this.isShowMoreSectionStatus = !this.isShowMoreSectionStatus;
    }

    readMoreStatusComment(key: string) {
        if (!this.isReadMoreSectionStatus)
            this.isReadMoreSectionStatus = {};

        this.isReadMoreSectionStatus[key] = Object.keys(this.isReadMoreSectionStatus).indexOf(key) > -1 ? !this.isReadMoreSectionStatus[key] : true;
    }

    goToFbrStep() {
        this.eventAggregator.publish(Messages.changeEntityItemStep, {
            step: VesselPreArrivalSteps.Fbr
        });
    }

    getPortCallStatusClass(status: PortCallStatusHistoryItemModel) {
        if (status && status.name) {
            return status.name == 'Reviewed (with warnings)' ? 'reviewed-with-warnings' : status.name.split(' ').join('-').toLowerCase();
        }
    }

    openAttachmentsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PreArrivalAttachmentsDialog,
            {
                vesselName: this.vesselPreArrivalDetails.vesselName,
                vesselIdentifier: this.vesselPreArrivalDetails.identifier
            }, true, false, false, "right");
    }

    openRequestFormsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortAndTerminalsRequestFormsDialog,
            {
                portSKey: this.vesselPreArrivalDetails.portSKey,
                vesselBKey: this.vesselPreArrivalDetails.vesselSKey,
                vesselPortCallId: this.vesselPreArrivalDetails.vesselPortCallId,
                vesselName: this.vesselPreArrivalDetails.vesselName,
                vesselImoNumber: this.vesselPreArrivalDetails.identifier,
                onSave: () => {
                    this.refreshVesselPreArrival({ vesselIds: [this.vesselPreArrivalDetails.vesselId], vesselPortCallId: this.vesselPreArrivalDetails.vesselPortCallId });
                }
            }, true, false, false, "right");
    }

    viewAllForms() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortCallFormsDialog,
            {
                vesselSKey: this.vesselPreArrivalDetails.vesselSKey,
                vesselName: this.vesselPreArrivalDetails.vesselName,
                vesselImoNumber: this.vesselPreArrivalDetails.imo
            }, true, false, false, "right")
    }

    requestAFullVet() {

        this.autoScreeningService.getAutoScreening(this.vesselPreArrivalDetails.vetId, false).then(response => {
            const linkFullVetitem = CloneHelper.clone(response);
            // set cloned autoscreen vetId to 0 so that we create a new one from autoscreen
            linkFullVetitem.vetId = 0;
            // init these two properties as false because they does not exist in autoscreens
            linkFullVetitem.vetForSTS = false;
            linkFullVetitem.charterTypeId = null;
            this.manageVesselService.showChooseDialog(
                {
                    vessel: <VesselDetailModel>{
                        vetRequiresMoreInformationEmails: linkFullVetitem.vetRequiresMoreInformationEmails,
                        documentOfComplianceEmail: linkFullVetitem.documentOfComplianceEmail,
                        cargoTypeCategoryId: linkFullVetitem.cargoTypeCategoryId,
                        vesselId: linkFullVetitem.vesselId,
                        internationalMaritimeOrganizationNumber: linkFullVetitem.internationalMaritimeOrganizationNumber,
                        name: linkFullVetitem.vesselName
                    },
                    vettingType: VettingType.FullVet,
                    parentVetId: this.vesselPreArrivalDetails.vetId,
                    vet: linkFullVetitem,
                    linkFullVetCheck: true

                });
        });
    }

    navigateToFullVet() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Vet, { id: this.vesselPreArrivalDetails.fullVetId });
    }
}
